import { useState, useEffect } from "react";
import {
  Modal, Box, InputBase, IconButton, Typography,
  Chip, Avatar
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { DataGrid } from "@mui/x-data-grid";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../theme";
import { getUniformesStockmin } from "../../services/uniformes.services"; 
import { getApiImagen } from "../../utils/getApiBaseUrl";

const ModalStockminU = ({ open, onClose, onSelectUniforme }) => {
  const [uniformes, setUniformes] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const columns = [
    {
    field: "img",
    headerName: "Img",
    width: 150,
    sortable: false,
    renderCell: (params) => (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          height: "100%",
        }}
      >
        <img
          src={`${getApiImagen()}${params.value}`}
          alt="Uniforme"
          style={{
            maxWidth: "100%",   // ocupa todo el ancho disponible
            maxHeight: "100%",  // ocupa todo el alto disponible
            objectFit: "contain", // asegura que entre completa
          }}
        />
      </Box>
    ),
  },
    {
      field: "id_producto",
      headerName: "ID",
      width: 70,
      renderCell: (p) => (
        <Typography sx={{ fontSize: 16, color: "#64748b", fontFamily: "monospace" }}>
          #{p.value}
        </Typography>
      ),
    },
     { 
    field: "descripcion", 
    headerName: "Descripción", 
    flex: 1,
    renderCell: (p) => (
      <Typography sx={{ fontSize: 18, color: "#f8fafc" }}>{p.value}</Typography>
    ),
  },
    {
      field: "talla",
      headerName: "Talla",
      width: 80,
      renderCell: (p) => (
        <Chip
          label={p.value}
          size="small"
          sx={{
            bgcolor: "rgba(99,102,241,0.12)",
            color: "#a5b4fc",
            border: "1px solid rgba(99,102,241,0.25)",
            fontWeight: 700,
            fontSize: 18,
          }}
        />
      ),
    },
    {
      field: "cantidad_actual",
      headerName: "Stock Actual",
      width: 110,
      renderCell: (params) => {
        const actual = Number(params.value);
        const min = Number(params.row.cant_min);
        const isCritical = actual < min * 0.5;
        return (
          <Chip
            label={actual}
            size="small"
            sx={{
              bgcolor: isCritical ? "rgba(239,68,68,0.12)" : "rgba(234,179,8,0.12)",
              color: isCritical ? "#f87171" : "#fbbf24",
              border: `1px solid ${isCritical ? "rgba(239,68,68,0.3)" : "rgba(234,179,8,0.3)"}`,
              fontWeight: 700,
              fontSize: 18,
            }}
          />
        );
      },
    },
    {
      field: "cant_min",
      headerName: "Mínimo",
      width: 90,
      renderCell: (p) => (
        <Typography sx={{ fontSize: 18, color: "#94a3b8" }}>{p.value}</Typography>
      ),
    },
    { 
    field: "empresa", 
    headerName: "Empresa", 
    flex: 1,
    renderCell: (p) => (
      <Typography sx={{ fontSize: 18, color: "#94a3b8" }}>{p.value}</Typography>
    ),
  },
  ];

  useEffect(() => {
    if (!open) return;
    const fetchData = async () => {
      try {
        const response = await getUniformesStockmin();
        setUniformes(response);
        setFilteredData(response);
      } catch (error) {
        console.error("Error al obtener stock crítico:", error);
      }
    };
    fetchData();
  }, [open]);

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchValue(val);
    const lower = val.toLowerCase();
    setFilteredData(
      uniformes.filter(
        (u) =>
          u.descripcion?.toLowerCase().includes(lower) ||
          u.talla?.toLowerCase().includes(lower) ||
          u.empresa?.toLowerCase().includes(lower)
      )
    );
  };

  const handleRowDoubleClick = (params) => {
    onSelectUniforme?.(params.row);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      BackdropProps={{ sx: { backdropFilter: "blur(6px)", bgcolor: "rgba(0,0,0,0.75)" } }}
    >
      <Box
        sx={{
          width: { xs: "95vw", md: 860 },
          bgcolor: "#151820",
          border: "1px solid rgba(255,100,50,0.2)",
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 0 80px rgba(255,80,30,0.1), 0 25px 60px rgba(0,0,0,0.6)",
          animation: "slideIn .3s cubic-bezier(.16,1,.3,1)",
          "@keyframes slideIn": {
            from: { transform: "translateY(24px)", opacity: 0 },
            to: { transform: "translateY(0)", opacity: 1 },
          },
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #1e0a00 0%, #1a0e0e 50%, #151820 100%)",
            px: 3.5,
            py: 2.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid rgba(255,100,50,0.15)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "10px",
                background: "linear-gradient(135deg, #ff4500, #ff6b35)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 15px rgba(255,69,0,0.4)",
              }}
            >
              <WarningAmberRoundedIcon sx={{ color: "#fff", fontSize: 22 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: 17, color: "#fff", letterSpacing: 0.3 }}>
                Stock Crítico de Uniformes
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.3 }}>
                Productos por debajo del mínimo requerido
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              border: "1px solid rgba(255,255,255,0.1)",
              bgcolor: "rgba(255,255,255,0.05)",
              color: "#94a3b8",
              "&:hover": { bgcolor: "rgba(255,69,0,0.2)", borderColor: "rgba(255,69,0,0.4)", color: "#ff6b35" },
            }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* SEARCH BAR */}
        <Box
          sx={{
            px: 3.5,
            py: 1.5,
            display: "flex",
            gap: 1.5,
            alignItems: "center",
            borderBottom: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              bgcolor: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "8px",
              px: 1.5,
              "&:focus-within": { borderColor: "rgba(255,100,50,0.5)", bgcolor: "rgba(255,100,50,0.04)" },
            }}
          >
            <SearchIcon sx={{ color: "#64748b", fontSize: 18, mr: 1 }} />
            <InputBase
              placeholder="Buscar por descripción, talla o empresa..."
              value={searchValue}
              onChange={handleSearch}
              sx={{ flex: 1, fontSize: 16, color: "#e2e8f0", "& input::placeholder": { color: "#475569" } }}
            />
          </Box>
          <Chip
            label={`⚠ ${filteredData.length} críticos`}
            size="small"
            sx={{
              bgcolor: "rgba(127,29,29,0.6)",
              color: "#fca5a5",
              border: "1px solid rgba(252,165,165,0.2)",
              fontWeight: 600,
              fontSize: 14,
            }}
          />
        </Box>

        {/* DATAGRID */}
        <Box
          sx={{
            height: "55vh",
            "& .MuiDataGrid-root": { border: "none", color: "#cbd5e1" },
            "& .MuiDataGrid-cell": { borderBottom: "1px solid rgba(255,255,255,0.04)" },
            "& .MuiDataGrid-columnHeaders": {
              bgcolor: "#1a0e0e",
              borderBottom: "1px solid rgba(255,100,50,0.2)",
              color: "#ff6b35",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.8,
              textTransform: "uppercase",
            },
            "& .MuiDataGrid-virtualScroller": { bgcolor: "#151820" },
            "& .MuiDataGrid-footerContainer": { borderTop: "1px solid rgba(255,255,255,0.06)", bgcolor: "#151820" },
            "& .MuiDataGrid-row:hover": { bgcolor: "rgba(255,100,50,0.06) !important" },
          }}
        >
          <DataGrid
            rows={filteredData}
            columns={columns}
            getRowId={(row) => row.id_producto}
            onRowDoubleClick={handleRowDoubleClick}
            disableSelectionOnClick
            rowHeight={80}
          />
        </Box>

        {/* FOOTER */}
        <Box
          sx={{
            px: 3.5,
            py: 1.5,
            borderTop: "1px solid rgba(255,255,255,0.06)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >

          <Box
            component="button"
            onClick={onClose}
            sx={{
              background: "linear-gradient(135deg, #ff4500, #ff6b35)",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              px: 2.5,
              py: 1,
              fontWeight: 700,
              fontSize: 13,
              cursor: "pointer",
              letterSpacing: 0.3,
              "&:hover": { transform: "translateY(-1px)", boxShadow: "0 6px 20px rgba(255,69,0,0.4)" },
              transition: "all .2s",
            }}
          >
            Cerrar
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ModalStockminU;